import React from "react";
import { DEMO_FILTER_OPTIONS } from "./utils/demographicLabels";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import QuestionFilterComponent from "./QuestionFilterComponent";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonIcon from "@mui/icons-material/Person";
import CakeIcon from "@mui/icons-material/Cake";
import GroupsIcon from "@mui/icons-material/Groups";
import HowToVoteIcon from "@mui/icons-material/HowToVote";
import SchoolIcon from "@mui/icons-material/School";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TopAreasPanel from "./TopAreasPanel";
import TuneIcon from "@mui/icons-material/Tune";
import AreaSearch from "./AreaSearch";
import {
  Box, Typography, Select, MenuItem, ListSubheader, Stack,
  Tooltip, Accordion, AccordionSummary, AccordionDetails, Collapse,
  ButtonBase, ToggleButtonGroup, ToggleButton,
} from "@mui/material";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Cell,
  Tooltip as RechartsTooltip,
  CartesianGrid,
} from "recharts";

// ─── Design tokens ────────────────────────────────────────────────────────────
const T = {
  bg: "#ffffff",
  surface: "#f8f9fc",
  surfaceHover: "#f0f2f7",
  border: "rgba(0,0,0,0.07)",
  borderStrong: "rgba(0,0,0,0.13)",
  text: "#1a1d23",
  textMuted: "rgb(85, 85, 85)",
  textFaint: "rgb(128, 128, 128)",
  accent: "#2563eb",
  accentDim: "rgba(37,99,235,0.08)",
  accentGlow: "rgba(37,99,235,0.04)",
  chip: "#f3f4f6",
  chipActive: "rgba(37,99,235,0.1)",
  chipActiveBorder: "rgba(37,99,235,0.35)",
};

// ─── Shared sub-components ────────────────────────────────────────────────────
const FilterChip = ({ label, active, onClick }) => (
  <ButtonBase
    onClick={onClick}
    sx={{
      px: 1.25, py: 0.5, borderRadius: "6px",
      border: "1px solid",
      borderColor: active ? T.chipActiveBorder : T.border,
      bgcolor: active ? T.chipActive : T.chip,
      color: active ? T.accent : T.textMuted,
      fontSize: "11px", fontWeight: active ? 600 : 400,
      fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.02em",
      transition: "all 0.15s ease",
      "&:hover": {
        borderColor: active ? T.chipActiveBorder : T.borderStrong,
        bgcolor: active ? T.chipActive : T.surfaceHover,
        color: active ? T.accent : T.text,
      },
    }}
  >
    {label}
  </ButtonBase>
);

const DemographicGroup = ({ icon: Icon, label, iconColor, children }) => (
  <Box>
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
      <Icon sx={{ fontSize: 13, color: iconColor || T.textFaint }} />
      <Typography sx={{
        fontSize: "10px", fontWeight: 600, color: T.textFaint,
        letterSpacing: "0.08em", textTransform: "uppercase",
        fontFamily: "'DM Sans', sans-serif",
      }}>
        {label}
      </Typography>
    </Stack>
    <Stack direction="row" flexWrap="wrap" gap={0.5}>{children}</Stack>
  </Box>
);

const StyledAccordion = ({ children, defaultExpanded, ...props }) => (
  <Accordion
    defaultExpanded={defaultExpanded}
    elevation={0}
    disableGutters
    {...props}
    sx={{
      bgcolor: "transparent", border: "none",
      borderBottom: `1px solid ${T.border}`,
      borderRadius: "0 !important",
      "&:before": { display: "none" },
      "&.Mui-expanded": { margin: 0 },
      ...props.sx,
    }}
  >
    {children}
  </Accordion>
);

const StyledAccordionSummary = ({ children, ...props }) => (
  <AccordionSummary
    expandIcon={<ExpandMoreIcon sx={{ fontSize: 16, color: T.textFaint }} />}
    {...props}
    sx={{
      px: 2.5, py: 0, minHeight: "44px !important",
      "&.Mui-expanded": { minHeight: "44px !important" },
      "& .MuiAccordionSummary-content": { my: "0 !important", alignItems: "center" },
      "&:hover": { bgcolor: T.accentGlow },
      transition: "background 0.15s ease",
    }}
  >
    {children}
  </AccordionSummary>
);

const SectionLabel = ({ children }) => (
  <Typography sx={{
    fontSize: "11px", fontWeight: 700, color: T.textFaint,
    letterSpacing: "0.1em", textTransform: "uppercase",
    fontFamily: "'DM Sans', sans-serif",
  }}>
    {children}
  </Typography>
);

// ─── Main Sidebar ─────────────────────────────────────────────────────────────
const Sidebar = ({
  isPanelOpen = true,
  togglePanel = () => {},
  sectionTitle = "Electorate Profiles",
  groupsById,
  selectedVariable,
  onVariableChange,
  genderFilter,       onGenderChange,
  ageFilter,          onAgeChange,
  ethnicityFilter,    onEthnicityChange,
  partyFilter,        onPartyChange,
  educationFilter,    onEducationChange,
  preferenceFilter,
  onPreferenceFilterChange,
  selectedFeatureData,
  selectedConstName,
  onClearAll,
  ActionMode,
  handleActionModeChange,
  rankedItems,
  onAreaSelect,
  hoverArea,
  handlehoverAreaChange,
  compareWaves,
  selectedFeature,
  user,
  ActionTargetVariables,
  ActionTargetSubVariables,
  ActionTargetConcentration,
  onActionTargetVariableChange,
  handleActionTargetSubVariableChange,
  handleActionTargetConcentrationChange,
  selectedDemographics,
}) => {
  const isSel = (val, target) => val === target;
  const toggleVal = (curr, next) => curr === next ? null : next;

  const [compareChartMode, setCompareChartMode] = React.useState("bars");
  const [activeWaveKeys, setActiveWaveKeys] = React.useState(null);
  const [trendAnimate, setTrendAnimate] = React.useState(true);
  const [hoveredTrendKey, setHoveredTrendKey] = React.useState(null);
  const [showNationalLine, setShowNationalLine] = React.useState(false);

  React.useEffect(() => {
    setCompareChartMode(compareWaves ? "trend" : "bars");
  }, [compareWaves, selectedFeature]);

  React.useEffect(() => {
    setActiveWaveKeys(null);
  }, [selectedVariable]);

  const trendAnimSignature = React.useMemo(() => {
    const ws = Array.isArray(selectedFeatureData?.waveSeries) ? selectedFeatureData.waveSeries : [];
    return [
      selectedVariable || "", selectedFeature?.name || "",
      compareChartMode, ws.length,
      activeWaveKeys ? Array.from(activeWaveKeys).sort().join(",") : "__all__",
    ].join("|");
  }, [selectedVariable, selectedFeature, compareChartMode, selectedFeatureData?.waveSeries, activeWaveKeys]);

  React.useEffect(() => {
    setTrendAnimate(true);
    const t = setTimeout(() => setTrendAnimate(false), 450);
    return () => clearTimeout(t);
  }, [trendAnimSignature]);

  const selected = selectedVariable ? groupsById?.[selectedVariable] : null;

  const variableKeys = Object.keys(groupsById || {}).filter((v) => !groupsById[v]?.hidden);

  const CATEGORY_ORDER = ["political","company"];
  const CATEGORY_LABELS = { company: "Business Research", political: "Political" };
  const groupedVariableKeys = CATEGORY_ORDER.reduce((acc, cat) => {
    acc[cat] = variableKeys.filter((v) => groupsById[v]?.category === cat);
    return acc;
  }, {});
  const uncategorisedKeys = variableKeys.filter((v) => !groupsById[v]?.category);

  return (
    <Box sx={{ position: "relative", height: "100%", display: "flex", flexDirection: "row", zIndex: 1200 }}>

      {/* ── Main panel ──────────────────────────────────────────────────── */}
      <Box
        sx={{
          width: isPanelOpen ? "100%" : "0px",
          height: "calc(100vh - 64px)",
          bgcolor: T.bg,
          boxShadow: isPanelOpen ? "4px 0 20px rgba(0,0,0,0.08)" : "none",
          transition: "width 320ms cubic-bezier(0.22, 1, 0.36, 1)",
          opacity: isPanelOpen ? 1 : 0,
          pointerEvents: isPanelOpen ? "auto" : "none",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        {/* ── Sticky header ─────────────────────────────────────────────── */}
        <Box sx={{ position: "sticky", top: 0, zIndex: 10, bgcolor: T.bg, borderBottom: `1px solid ${T.border}` }}>
          <Box sx={{ py: 1.5, px: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Typography variant="h6" sx={{
                fontWeight: 700, color: T.text,
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "15px", letterSpacing: "-0.01em",
              }}>
                {sectionTitle}
              </Typography>
            </Box>
          </Box>

          {/* Explore / Filter mode toggle */}
          <Box sx={{ display: "flex", px: 2.5, pb: 1.5, gap: 0.75 }}>
            {[
              { key: "explore", label: "Explore Variable" },
              { key: "filter", label: "Find Target Areas" },
            ].map(({ key, label }) => (
              <ButtonBase
                key={key}
                disableRipple
                onClick={() => handleActionModeChange(key)}
                sx={{
                  flex: 1, py: 0.85, px: 1.5, borderRadius: "8px",
                  border: "1px solid",
                  borderColor: ActionMode === key ? T.chipActiveBorder : T.border,
                  bgcolor: ActionMode === key ? T.chipActive : T.chip,
                  color: ActionMode === key ? T.accent : T.textMuted,
                  fontSize: "12px", fontWeight: ActionMode === key ? 700 : 400,
                  fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.01em",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: ActionMode === key ? T.chipActiveBorder : T.borderStrong,
                    bgcolor: ActionMode === key ? T.chipActive : T.surfaceHover,
                    color: ActionMode === key ? T.accent : T.text,
                  },
                  "&:focus": { outline: "none" },
                }}
              >
                {label}
              </ButtonBase>
            ))}
          </Box>
        </Box>

        {/* ── Explore mode ─────────────────────────────────────────────── */}
        {ActionMode === "explore" && (
          <Box sx={{
            flex: 1, overflowY: "auto", overflowX: "hidden",
            "&::-webkit-scrollbar": { width: "4px" },
            "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
            "&::-webkit-scrollbar-thumb": { bgcolor: T.border, borderRadius: "2px" },
          }}>

            {/* Variable selector */}
            <Box sx={{ p: 2.5, borderBottom: `1px solid ${T.border}` }}>
              <SectionLabel>Data Variable</SectionLabel>
              <Box sx={{ mt: 1.25 }}>
                <Select
                  value={selectedVariable ?? variableKeys[0] ?? ""}
                  onChange={(e) => onVariableChange?.(e.target.value)}
                  displayEmpty
                  size="small"
                  fullWidth
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: T.surface,
                        border: `1px solid ${T.borderStrong}`,
                        borderRadius: "10px",
                        mt: 0.5,
                        boxShadow: "0 16px 48px rgba(0,0,0,0.6)",
                        "& .MuiMenuItem-root": {
                          fontSize: "13px", color: T.textMuted,
                          fontFamily: "'DM Sans', sans-serif",
                          py: 0.75, px: 2, borderRadius: "6px", mx: 0.5,
                          "&:hover": { bgcolor: T.surfaceHover, color: T.text },
                          "&.Mui-selected": { bgcolor: T.accentDim, color: T.accent, fontWeight: 600 },
                        },
                      },
                    },
                  }}
                  sx={{
                    borderRadius: "8px", bgcolor: T.chip,
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: T.border, transition: "border-color 0.15s" },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: T.borderStrong },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: T.accent },
                    "& .MuiSelect-select": {
                      color: T.text, fontSize: "13px",
                      fontFamily: "'DM Sans', sans-serif", fontWeight: 500,
                      textAlign: "left !important", display: "flex !important",
                      alignItems: "center !important", py: 1.1,
                    },
                    "& .MuiSvgIcon-root": { color: T.textFaint },
                  }}
                >
                  {CATEGORY_ORDER.flatMap((cat) => {
                    const vars = groupedVariableKeys[cat];
                    if (!vars?.length) return [];
                    return [
                      <ListSubheader key={`hdr-${cat}`} disableSticky sx={{
                        fontSize: "10px", fontWeight: 700,
                        fontFamily: "'DM Sans', sans-serif",
                        letterSpacing: "0.07em", textTransform: "uppercase",
                        color: T.textFaint, lineHeight: "28px",
                        bgcolor: T.surface, px: 2,
                      }}>
                        {CATEGORY_LABELS[cat]}
                      </ListSubheader>,
                      ...vars.map((v) => (
                        <MenuItem key={v} value={v}>{groupsById[v]?.label || v}</MenuItem>
                      )),
                    ];
                  })}
                  {uncategorisedKeys.map((v) => (
                    <MenuItem key={v} value={v}>{groupsById[v]?.label || v}</MenuItem>
                  ))}
                </Select>
              </Box>

              {selected?.fulltext && (
                <Box sx={{ mt: 1.5 }}>
                  <Stack direction="row" alignItems="flex-start" spacing={0.75}>
                    <InfoOutlinedIcon sx={{ fontSize: 13, color: T.textFaint, mt: "2px", flexShrink: 0 }} />
                    <Collapse in timeout="auto" unmountOnExit>
                      <Typography sx={{
                        fontSize: "12px", color: T.textFaint,
                        lineHeight: 1.6, fontFamily: "'DM Sans', sans-serif",
                      }}>
                        {selected.fulltext}
                      </Typography>
                    </Collapse>
                  </Stack>
                </Box>
              )}

              <Box sx={{ mt: 1.5 }}>
                <QuestionFilterComponent
                  groupsById={groupsById}
                  selectedVariable={selectedVariable}
                  onVariableChange={onVariableChange}
                  preferenceFilter={preferenceFilter}
                  onPreferenceFilterChange={onPreferenceFilterChange}
                  ActionMode={ActionMode}
                  selectedFeatureData={selectedFeatureData}
                />
              </Box>
            </Box>

            {/* Demographics accordion */}
            <StyledAccordion>
              <StyledAccordionSummary>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TuneIcon sx={{ fontSize: 14, color: T.textFaint }} />
                  <SectionLabel>Filter by Demographics</SectionLabel>
                </Stack>
              </StyledAccordionSummary>
              <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0.5 }}>
                <Typography sx={{ fontSize: "12px", color: T.textFaint, mb: 1.75, fontFamily: "'DM Sans', sans-serif" }}>
                  Explore how different groups respond.
                </Typography>
                <Stack spacing={1.75}>
                  <Stack direction="row" spacing={2}>
                    <Box sx={{ flex: 1 }}>
                      <DemographicGroup icon={PersonIcon} label="Gender" iconColor="#60a5fa">
                        {DEMO_FILTER_OPTIONS.gender.map(([val, lbl]) => (
                          <FilterChip key={val} label={lbl} active={isSel(genderFilter, val)}
                            onClick={() => onGenderChange?.(toggleVal(genderFilter, val))} />
                        ))}
                      </DemographicGroup>
                    </Box>
                    <Box sx={{ flex: 1.5 }}>
                      <DemographicGroup icon={CakeIcon} label="Age" iconColor="#a78bfa">
                        {DEMO_FILTER_OPTIONS.age.map(([val, lbl]) => (
                          <FilterChip key={val} label={lbl} active={isSel(ageFilter, val)}
                            onClick={() => onAgeChange?.(toggleVal(ageFilter, val))} />
                        ))}
                      </DemographicGroup>
                    </Box>
                  </Stack>

                  <DemographicGroup icon={SchoolIcon} label="Education" iconColor="#fbbf24">
                    {DEMO_FILTER_OPTIONS.education.map(([val, lbl]) => (
                      <FilterChip key={val} label={lbl} active={isSel(educationFilter, val)}
                        onClick={() => onEducationChange?.(toggleVal(educationFilter, val))} />
                    ))}
                  </DemographicGroup>

                  <DemographicGroup icon={GroupsIcon} label="Ethnicity" iconColor="#34d399">
                    {DEMO_FILTER_OPTIONS.ethnicity.map(([val, lbl]) => (
                      <FilterChip key={val} label={lbl} active={isSel(ethnicityFilter, val)}
                        onClick={() => onEthnicityChange?.(toggleVal(ethnicityFilter, val))} />
                    ))}
                  </DemographicGroup>

                  <DemographicGroup icon={HowToVoteIcon} label="Past Vote" iconColor="#f87171">
                    {DEMO_FILTER_OPTIONS.party.map(([val, lbl]) => (
                      <FilterChip key={val} label={lbl} active={isSel(partyFilter, val)}
                        onClick={() => onPartyChange?.(toggleVal(partyFilter, val))} />
                    ))}
                  </DemographicGroup>
                </Stack>

                {onClearAll && (
                  <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                    <ButtonBase
                      onClick={onClearAll}
                      sx={{
                        fontSize: "11px", color: T.textFaint,
                        fontFamily: "'DM Sans', sans-serif", fontWeight: 500,
                        letterSpacing: "0.03em", px: 1.25, py: 0.5,
                        borderRadius: "6px", border: `1px solid ${T.border}`,
                        transition: "all 0.15s",
                        "&:hover": { color: T.text, borderColor: T.borderStrong, bgcolor: T.surfaceHover },
                      }}
                    >
                      Clear all
                    </ButtonBase>
                  </Box>
                )}
              </AccordionDetails>
            </StyledAccordion>

            {/* Compare by Geographies chart */}
            {(() => {
              const group = groupsById?.[selectedVariable];
              if (!group) return null;

              const fmtVal = (v) =>
                v == null || !Number.isFinite(v) ? "—" : `${v.toFixed(1)}%`;

              // Build label→color lookup from group config
              const labelOf = (k) => group.labels?.[k] || group.full_labels?.[k] || k;
              const toArrPol = (v) => Array.isArray(v) ? v : v != null ? [v] : [];
              const colorOf = (k) => {
                if (group.colors?.[k]) return group.colors[k];
                if (group.type === "likert" && group.polarity) {
                  if (toArrPol(group.polarity.pos).includes(k)) return "#2e7d32";
                  if (toArrPol(group.polarity.neg).includes(k)) return "#c62828";
                  return "#9ca3af";
                }
                return "#6e6e6e";
              };

              // Build combined rows from selectedFeatureData
              const toValMap2 = (arr) => {
                const m = new Map();
                (arr || []).forEach(({ key, value }) => {
                  const n = Number(value);
                  m.set(String(key), value != null && Number.isFinite(n) ? n : null);
                });
                return m;
              };

              let combined;
              let isNetChart = false;

              if (group.type === "likert_grid") {
                const natMap = toValMap2(selectedFeatureData?.national);
                const constMap = toValMap2(selectedFeatureData?.constituency);
                const selMap = toValMap2(selectedFeatureData?.selected);
                const [pfSvId] = String(preferenceFilter || "").split("|");
                const isDrilled = pfSvId && pfSvId !== "compare" && group.subVariables?.includes(pfSvId);

                if (isDrilled) {
                  const subVar = groupsById?.[pfSvId];
                  if (subVar) {
                    const pos = toArrPol(subVar.polarity?.pos);
                    const neu = toArrPol(subVar.polarity?.neu);
                    const neg = toArrPol(subVar.polarity?.neg);
                    const dk = toArrPol(subVar.polarity?.dk);
                    const seenPol = new Set([...pos, ...neu, ...neg, ...dk]);
                    const keyOrder = [...pos, ...neu, ...neg, ...dk,
                      ...(subVar.keys || []).filter((k) => !seenPol.has(k))];
                    const svColorOf = (k) => {
                      if (pos.includes(k)) return "#2e7d32";
                      if (neg.includes(k)) return "#c62828";
                      return "#9ca3af";
                    };
                    combined = keyOrder
                      .filter((k) => natMap.has(k) || constMap.has(k) || selMap.has(k))
                      .map((k) => ({
                        label: subVar.labels?.[k] || subVar.full_labels?.[k] || k,
                        color: svColorOf(k),
                        national: natMap.get(k) ?? null,
                        constituency: constMap.get(k) ?? null,
                        selected: selMap.get(k) ?? null,
                      }));
                  } else {
                    combined = [];
                  }
                } else {
                  isNetChart = true;
                  const netScore = (svId, valMap) => {
                    const sub = groupsById?.[svId];
                    if (!sub) return null;
                    const pos = toArrPol(sub.polarity?.pos);
                    const neg = toArrPol(sub.polarity?.neg);
                    const hasData = [...pos, ...neg].some(
                      (k) => Number.isFinite(valMap.get(String(k)))
                    );
                    if (!hasData) return null;
                    const sumPos = pos.reduce((acc, k) => acc + (valMap.get(String(k)) ?? 0), 0);
                    const sumNeg = neg.reduce((acc, k) => acc + (valMap.get(String(k)) ?? 0), 0);
                    return sumPos - sumNeg;
                  };
                  const gridRows = (group.subVariables || [])
                    .map((svId) => ({
                      label: group.subLabels?.[svId] || groupsById?.[svId]?.label || svId,
                      color: group.subColors?.[svId] || "#6e6e6e",
                      national: netScore(svId, natMap),
                      constituency: netScore(svId, constMap),
                      selected: netScore(svId, selMap),
                    }))
                    .filter((r) => Number.isFinite(r.national) || Number.isFinite(r.constituency) || Number.isFinite(r.selected));
                  combined = group.fixedOrder
                    ? gridRows
                    : gridRows.sort((a, b) => {
                        const va = Number.isFinite(a.selected) ? a.selected : Number.isFinite(a.constituency) ? a.constituency : Number.isFinite(a.national) ? a.national : -Infinity;
                        const vb = Number.isFinite(b.selected) ? b.selected : Number.isFinite(b.constituency) ? b.constituency : Number.isFinite(b.national) ? b.national : -Infinity;
                        return vb - va;
                      });
                }
              } else {
                const byLabel = new Map();
                const upsert = (arr, field) => {
                  (arr || []).forEach(({ key, value }) => {
                    const lbl = labelOf(key);
                    const row = byLabel.get(lbl) || {
                      label: lbl, color: colorOf(key),
                      national: null, constituency: null, selected: null,
                    };
                    const num = Number(value);
                    row[field] = value != null && Number.isFinite(num) ? num : null;
                    byLabel.set(lbl, row);
                  });
                };
                upsert(selectedFeatureData?.national, "national");
                upsert(selectedFeatureData?.constituency, "constituency");
                upsert(selectedFeatureData?.selected, "selected");

                const orderedKeys = group.type === "likert"
                  ? (() => {
                      const pos = toArrPol(group.polarity?.pos);
                      const neu = toArrPol(group.polarity?.neu);
                      const neg = toArrPol(group.polarity?.neg);
                      const dk = toArrPol(group.polarity?.dk);
                      const preferred = [...pos, ...neu, ...neg, ...dk];
                      const seen = new Set(preferred.map(String));
                      return [...preferred, ...(group.keys || []).filter((k) => !seen.has(String(k)))];
                    })()
                  : (group.keys || []);
                const order = orderedKeys.map(labelOf);
                const allRows = Array.from(byLabel.values()).sort((a, b) => {
                  const ia = order.indexOf(a.label), ib = order.indexOf(b.label);
                  return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
                });
                combined = (group.type === "likert" || group.fixedOrder)
                  ? allRows
                  : allRows.sort((a, b) => {
                      const va = Number.isFinite(a.selected) ? a.selected : Number.isFinite(a.constituency) ? a.constituency : Number.isFinite(a.national) ? a.national : -Infinity;
                      const vb = Number.isFinite(b.selected) ? b.selected : Number.isFinite(b.constituency) ? b.constituency : Number.isFinite(b.national) ? b.national : -Infinity;
                      return vb - va;
                    });
              }

              const hasNational = combined.some((d) => Number.isFinite(d.national));
              const hasConstituency = Boolean(selectedConstName) && combined.some((d) => Number.isFinite(d.constituency));
              const hasSelected = Boolean(selectedFeature) && selectedFeature?.level !== "electorate" && combined.some((d) => Number.isFinite(d.selected));
              if (!hasNational && !hasConstituency && !hasSelected) return null;

              // Dynamic y-axis width
              const FONT = "12px 'DM Sans', sans-serif";
              const LINE_H = 14;
              const MAX_LINES = 2;
              const TICK_PAD = 10;
              const measure = (() => {
                const canvas = document.createElement("canvas");
                const ctx = canvas.getContext("2d");
                return (text) => { if (!ctx) return 0; ctx.font = FONT; return ctx.measureText(String(text ?? "")).width; };
              })();
              const MIN_W = 110, MAX_W = 170;
              const longest = combined.reduce((m, d) => Math.max(m, measure(d.label)), 0);
              const yAxisWidth = Math.min(MAX_W, Math.max(MIN_W, Math.ceil(longest + TICK_PAD)));
              const maxPx = yAxisWidth - TICK_PAD;

              const ellipsizeToWidth = (text, w) => {
                const ell = "…";
                let s = String(text ?? "").trim();
                if (!s) return ell;
                if (measure(s + ell) <= w) return s + ell;
                while (s.length && measure(s + ell) > w) s = s.slice(0, -1);
                return (s.trimEnd() || "") + ell;
              };
              const wrapLabel2Lines = (text, w) => {
                const s = String(text ?? "").trim();
                if (!s) return [""];
                if (measure(s) <= w) return [s];
                const words = s.split(/\s+/).filter(Boolean);
                let line1 = "", i = 0;
                for (; i < words.length; i++) {
                  const next = line1 ? `${line1} ${words[i]}` : words[i];
                  if (measure(next) <= w) line1 = next; else break;
                }
                if (!line1) return [ellipsizeToWidth(words[0], w)];
                if (i >= words.length) return [line1];
                let line2 = "";
                for (; i < words.length; i++) {
                  const next = line2 ? `${line2} ${words[i]}` : words[i];
                  if (measure(next) <= w) line2 = next;
                  else { line2 = ellipsizeToWidth(line2 || words[i], w); return [line1, line2]; }
                }
                if (measure(line2) > w) line2 = ellipsizeToWidth(line2, w);
                return [line1, line2];
              };
              const wrappedByLabel = new Map();
              combined.forEach((d) => wrappedByLabel.set(d.label, wrapLabel2Lines(d.label, maxPx)));
              const TickWrap = ({ x, y, payload }) => {
                const text = String(payload?.value ?? "");
                const lines = wrappedByLabel.get(text) || wrapLabel2Lines(text, maxPx);
                return (
                  <text x={x - 6} y={y} textAnchor="end" fontSize={12} fill={T.textMuted}
                    style={{ pointerEvents: "none", fontFamily: "'DM Sans', sans-serif" }}>
                    {lines.map((t, idx) => <tspan key={idx} x={x - 6} dy={idx === 0 ? 0 : LINE_H}>{t}</tspan>)}
                  </text>
                );
              };

              const seriesCount = [hasNational, hasConstituency, hasSelected].filter(Boolean).length || 1;
              const BAR_SIZE = 10, BAR_GAP = 6;
              const rowH = Math.max(LINE_H * MAX_LINES, seriesCount * BAR_SIZE + (seriesCount - 1) * BAR_GAP) + 10;
              const chartHeight = Math.max(260, combined.length * rowH + 30);

              const allVals = combined.flatMap((d) => [
                hasNational ? d.national : null,
                hasConstituency ? d.constituency : null,
                hasSelected ? d.selected : null,
              ]).filter(Number.isFinite);
              const rawMax = allVals.length ? Math.max(...allVals) : 100;
              const rawMin = allVals.length ? Math.min(...allVals) : 0;
              const maxVal = Math.min(100, Math.ceil(Math.max(10, rawMax)));
              const minVal = isNetChart ? Math.max(-100, Math.floor(Math.min(0, rawMin))) : 0;

              // Wave series (trend chart)
              const rawWaveSeries = Array.isArray(selectedFeatureData?.waveSeries) ? selectedFeatureData.waveSeries : [];
              const allWaveKeys = group.keys || [];
              const getKeyLabel = (k) => labelOf(k);
              const getKeyColor = (k) => colorOf(k);
              const visibleKeys = activeWaveKeys
                ? allWaveKeys.filter((k) => activeWaveKeys.has(k))
                : allWaveKeys;
              const effectiveKeys = visibleKeys.length > 0 ? visibleKeys : allWaveKeys;
              const trendWaveSeries = rawWaveSeries.filter((row) => {
                const vals = effectiveKeys.map((k) => Number(row?.[k])).filter((v) => Number.isFinite(v));
                return vals.length > 0 && vals.some((v) => Math.abs(v) > 1e-9);
              });
              const showTrendToggle = compareWaves && trendWaveSeries.length >= 2 && allWaveKeys.length > 0;
              const isTrendMode = showTrendToggle && compareChartMode === "trend";

              const toggleKey = (k) => {
                if (!activeWaveKeys) {
                  setActiveWaveKeys(new Set([k]));
                } else {
                  const next = new Set(activeWaveKeys);
                  if (next.has(k)) {
                    next.delete(k);
                    setActiveWaveKeys(next.size === 0 ? null : next);
                  } else {
                    next.add(k);
                    setActiveWaveKeys(next.size === allWaveKeys.length ? null : next);
                  }
                }
              };

              const selectedLabel = selectedFeature?.name || "Selected Area";

              return (
                <StyledAccordion defaultExpanded>
                  <StyledAccordionSummary>
                    <SectionLabel>Compare by Geographies</SectionLabel>
                  </StyledAccordionSummary>

                  <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0.5 }}>
                    {/* Legend */}
                    {!isTrendMode && (
                      <Stack direction="row" spacing={2} sx={{ mb: 1, flexWrap: "wrap", gap: 1 }}>
                        {[
                          hasNational && { label: "NZ Average", opacity: 0.35 },
                          hasConstituency && { label: selectedConstName, opacity: 0.65 },
                          hasSelected && { label: selectedLabel, opacity: 1 },
                        ].filter(Boolean).map(({ label, opacity }) => (
                          <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: T.accent, opacity }} />
                            <Typography sx={{
                              fontSize: "11px", color: T.textFaint,
                              fontFamily: "'DM Sans', sans-serif",
                              maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            }}>
                              {label}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    )}

                    {/* Bars / Trend toggle */}
                    {showTrendToggle && (
                      <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
                        {["bars", "trend"].map((mode) => {
                          const active = compareChartMode === mode;
                          return (
                            <ButtonBase
                              key={mode}
                              onClick={() => setCompareChartMode(mode)}
                              sx={{
                                px: 1.25, py: 0.5, borderRadius: "6px",
                                border: "1px solid",
                                borderColor: active ? T.chipActiveBorder : T.border,
                                bgcolor: active ? T.chipActive : T.chip,
                                color: active ? T.accent : T.textMuted,
                                fontSize: "11px", fontWeight: active ? 600 : 400,
                                fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.02em",
                                transition: "all 0.15s ease",
                                "&:hover": {
                                  borderColor: active ? T.chipActiveBorder : T.borderStrong,
                                  bgcolor: active ? T.chipActive : T.surfaceHover,
                                  color: active ? T.accent : T.text,
                                },
                              }}
                            >
                              {mode.charAt(0).toUpperCase() + mode.slice(1)}
                            </ButtonBase>
                          );
                        })}
                      </Stack>
                    )}

                    {/* Trend chart */}
                    {isTrendMode && (() => {
                      const hasNationalData = effectiveKeys.some((k) =>
                        trendWaveSeries.some((r) => Number.isFinite(Number(r?.[`${k}__nat`])))
                      );
                      return (
                        <>
                          {allWaveKeys.length > 1 && (
                            <Box sx={{ mb: 1 }}>
                              <Typography sx={{
                                fontSize: "0.65rem", fontWeight: 700, color: T.textFaint,
                                textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.5,
                              }}>
                                Show categories
                              </Typography>
                              <ToggleButtonGroup
                                value={activeWaveKeys ? Array.from(activeWaveKeys) : allWaveKeys}
                                onChange={(_e, newVal) => {
                                  if (!newVal || newVal.length === 0 || newVal.length === allWaveKeys.length) {
                                    setActiveWaveKeys(null);
                                  } else {
                                    setActiveWaveKeys(new Set(newVal));
                                  }
                                }}
                                size="small"
                                sx={{
                                  display: "flex", flexWrap: "wrap", borderRadius: 2, p: 0.3, gap: 0.25,
                                  "& .MuiToggleButton-root": {
                                    border: "none", borderRadius: 1.5, py: 0.3, px: 0.8,
                                    textTransform: "none", fontWeight: 600, fontSize: "0.7rem",
                                    color: "text.secondary", minWidth: 0, lineHeight: 1.3,
                                    fontFamily: "'DM Sans', sans-serif",
                                    "&.Mui-selected": {
                                      backgroundColor: "#fff", color: "text.primary",
                                      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                      "&:hover": { backgroundColor: "#fff" },
                                    },
                                    "&:hover": { backgroundColor: "rgba(0,0,0,0.05)" },
                                  },
                                }}
                              >
                                {allWaveKeys.map((k) => (
                                  <ToggleButton key={k} value={k}>
                                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: getKeyColor(k), mr: 0.5, flexShrink: 0 }} />
                                    {getKeyLabel(k)}
                                  </ToggleButton>
                                ))}
                              </ToggleButtonGroup>
                            </Box>
                          )}

                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75 }}>
                            <Typography sx={{ fontSize: "11px", color: T.textFaint, fontFamily: "'DM Sans', sans-serif" }}>
                              Trend across waves (%)
                            </Typography>
                            {hasNationalData && (
                              <ButtonBase
                                onClick={() => setShowNationalLine((v) => !v)}
                                sx={{
                                  px: 1, py: 0.35, borderRadius: "5px",
                                  border: "1px solid",
                                  borderColor: showNationalLine ? T.chipActiveBorder : T.border,
                                  bgcolor: showNationalLine ? T.chipActive : T.chip,
                                  color: showNationalLine ? T.accent : T.textMuted,
                                  fontSize: "10px", fontWeight: showNationalLine ? 600 : 400,
                                  fontFamily: "'DM Sans', sans-serif",
                                  display: "flex", alignItems: "center", gap: 0.5,
                                  transition: "all 0.15s ease",
                                  "&:hover": { borderColor: T.borderStrong, bgcolor: T.surfaceHover, color: T.text },
                                }}
                              >
                                <Box component="span" sx={{ display: "inline-block", width: 14, height: 0, borderTop: "2px dashed currentColor", verticalAlign: "middle", mr: 0.4 }} />
                                vs NZ Average
                              </ButtonBase>
                            )}
                          </Box>

                          <ResponsiveContainer width="100%" height={220}>
                            <LineChart
                              data={trendWaveSeries}
                              margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
                              onMouseLeave={() => setHoveredTrendKey(null)}
                            >
                              <XAxis dataKey="wave" tick={{ fontSize: 11, fill: T.textMuted }} tickFormatter={(v) => `Wave ${v}`} />
                              <YAxis tick={{ fontSize: 10, fill: T.textMuted }} width={40} tickFormatter={(v) => `${v.toFixed(0)}%`} domain={["auto", "auto"]} />
                              <CartesianGrid stroke={T.border} strokeDasharray="3 3" opacity={0.5} />
                              <RechartsTooltip
                                content={({ active, payload, label: ttLabel }) => {
                                  if (!active || !payload?.length) return null;
                                  const fmt = (v) => v == null || isNaN(v) ? "—" : `${v.toFixed(1)}%`;
                                  const waveIdx = trendWaveSeries.findIndex((r) => Number(r?.wave) === Number(ttLabel));
                                  const deltaForKey = (k) => {
                                    if (waveIdx <= 0) return null;
                                    const prev = Number(trendWaveSeries[waveIdx - 1]?.[k]);
                                    const curr = Number(trendWaveSeries[waveIdx]?.[k]);
                                    return Number.isFinite(prev) && Number.isFinite(curr) ? curr - prev : null;
                                  };
                                  const focused = hoveredTrendKey && effectiveKeys.includes(hoveredTrendKey) ? hoveredTrendKey : null;
                                  const visKeys = focused ? [focused] : effectiveKeys;
                                  return (
                                    <div style={{ background: "#fff", border: "1px solid rgba(0,0,0,0.1)", borderRadius: 8, padding: "8px 12px", boxShadow: "0 4px 16px rgba(0,0,0,0.1)", fontFamily: "'DM Sans', system-ui, sans-serif", minWidth: 160 }}>
                                      <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", marginBottom: 7, textTransform: "uppercase", letterSpacing: "0.06em" }}>Wave {ttLabel}</div>
                                      {visKeys.map((k) => {
                                        const val = Number(trendWaveSeries[waveIdx]?.[k]);
                                        const delta = deltaForKey(k);
                                        const dColor = delta > 0 ? "#16a34a" : delta < 0 ? "#dc2626" : "#9ca3af";
                                        return (
                                          <div key={k} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 4 }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                                              <div style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: getKeyColor(k), flexShrink: 0 }} />
                                              <span style={{ fontSize: 12, color: "#374151", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{getKeyLabel(k)}</span>
                                            </div>
                                            <div style={{ display: "flex", alignItems: "baseline", gap: 5, flexShrink: 0 }}>
                                              <span style={{ fontSize: 12, fontWeight: 700, color: "#1a1d23" }}>{fmt(val)}</span>
                                              {Number.isFinite(delta) && (
                                                <span style={{ fontSize: 10.5, fontWeight: 700, color: dColor }}>{delta > 0 ? "+" : ""}{delta.toFixed(1)}pp</span>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  );
                                }}
                              />
                              {effectiveKeys.map((k) => (
                                <React.Fragment key={k}>
                                  <Line type="monotone" dataKey={k} stroke="rgba(0,0,0,0)" strokeWidth={14} dot={false} activeDot={false} connectNulls isAnimationActive={false} onMouseOver={() => setHoveredTrendKey(k)} onMouseOut={() => setHoveredTrendKey(null)} />
                                  <Line
                                    type="monotone" dataKey={k}
                                    stroke={getKeyColor(k)}
                                    strokeWidth={hoveredTrendKey === k ? 3 : 2}
                                    strokeOpacity={hoveredTrendKey && hoveredTrendKey !== k ? 0.2 : 1}
                                    dot={(dp) => {
                                      const { cx, cy, value } = dp || {};
                                      if (!Number.isFinite(cx) || !Number.isFinite(cy) || !Number.isFinite(Number(value))) return null;
                                      return <circle cx={cx} cy={cy} r={hoveredTrendKey === k ? 3 : 2} fill={getKeyColor(k)} stroke="#fff" strokeWidth={1} />;
                                    }}
                                    activeDot={{ r: 4 }}
                                    isAnimationActive={trendAnimate}
                                    animationDuration={450}
                                    connectNulls
                                    onMouseOver={() => setHoveredTrendKey(k)}
                                    onMouseOut={() => setHoveredTrendKey(null)}
                                  />
                                  {showNationalLine && hasNationalData && (
                                    <Line key={`${k}__nat`} type="monotone" dataKey={`${k}__nat`} stroke={getKeyColor(k)} strokeWidth={1.5} strokeDasharray="4 3" strokeOpacity={hoveredTrendKey && hoveredTrendKey !== k ? 0.12 : 0.5} dot={false} activeDot={false} connectNulls isAnimationActive={false} />
                                  )}
                                </React.Fragment>
                              ))}
                            </LineChart>
                          </ResponsiveContainer>
                        </>
                      );
                    })()}

                    {/* Bar chart */}
                    {!isTrendMode && (
                      <ResponsiveContainer width="100%" height={chartHeight}>
                        <BarChart
                          data={combined}
                          layout="vertical"
                          margin={{ top: 8, right: 24, left: 12, bottom: 6 }}
                          barCategoryGap={22}
                          barGap={6}
                        >
                          <XAxis type="number" domain={[minVal, maxVal]} hide />
                          <YAxis
                            type="category"
                            dataKey="label"
                            width={yAxisWidth}
                            tick={<TickWrap />}
                          />
                          <CartesianGrid horizontal={false} strokeDasharray="3 3" opacity={0.2} />
                          <RechartsTooltip
                            content={({ active, payload, label }) => {
                              if (!active || !payload?.length) return null;
                              const natEntry = payload.find((p) => p.dataKey === "national");
                              const constEntry = payload.find((p) => p.dataKey === "constituency");
                              const selEntry = payload.find((p) => p.dataKey === "selected");
                              const rowColor = combined.find((r) => r.label === label)?.color || payload[0]?.payload?.color || "#999";
                              const entries = [
                                hasNational && { name: "NZ Average", value: natEntry?.value, opacity: 0.35 },
                                hasConstituency && { name: selectedConstName, value: constEntry?.value, opacity: 0.65 },
                                hasSelected && { name: selectedLabel, value: selEntry?.value, opacity: 1 },
                              ].filter(Boolean);
                              return (
                                <div style={{ background: "#fff", border: `1px solid ${T.borderStrong}`, borderRadius: 10, padding: "8px 10px", boxShadow: "0 8px 24px rgba(0,0,0,0.12)", fontSize: 12, lineHeight: 1.4, maxWidth: 240, fontFamily: "'DM Sans', sans-serif" }}>
                                  <div style={{ fontWeight: 600, marginBottom: 6, wordWrap: "break-word", whiteSpace: "normal", color: T.text }}>{label}</div>
                                  <div style={{ display: "grid", gridTemplateColumns: "12px 1fr auto", columnGap: 6, rowGap: 4, alignItems: "center" }}>
                                    {entries.map((e, i) => (
                                      <React.Fragment key={i}>
                                        <div style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: rowColor, opacity: e.opacity }} />
                                        <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, fontWeight: 500, color: T.textMuted }} title={e.name}>{e.name}</div>
                                        <div style={{ textAlign: "right", fontWeight: 600, whiteSpace: "nowrap", color: T.text }}>{fmtVal(e.value)}</div>
                                      </React.Fragment>
                                    ))}
                                  </div>
                                </div>
                              );
                            }}
                          />
                          {hasNational && (
                            <Bar dataKey="national" barSize={10} radius={[4, 4, 4, 4]}>
                              {combined.map((row, i) => <Cell key={`n-${i}`} fill={row.color} fillOpacity={0.35} />)}
                            </Bar>
                          )}
                          {hasConstituency && (
                            <Bar dataKey="constituency" barSize={10} radius={[4, 4, 4, 4]}>
                              {combined.map((row, i) => <Cell key={`c-${i}`} fill={row.color} fillOpacity={0.65} />)}
                            </Bar>
                          )}
                          {hasSelected && (
                            <Bar dataKey="selected" barSize={10} radius={[4, 4, 4, 4]}>
                              {combined.map((row, i) => <Cell key={`s-${i}`} fill={row.color} fillOpacity={1} />)}
                            </Bar>
                          )}
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </AccordionDetails>
                </StyledAccordion>
              );
            })()}

          </Box>
        )}

        {/* ── Filter mode ───────────────────────────────────────────────── */}
        {ActionMode === "filter" && (
          <Box sx={{
            flex: 1, overflowY: "auto", overflowX: "hidden",
            "&::-webkit-scrollbar": { width: "4px" },
            "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
            "&::-webkit-scrollbar-thumb": { bgcolor: T.border, borderRadius: "2px" },
          }}>

            {/* Find Audiences: variable + response + concentration */}
            <StyledAccordion defaultExpanded>
              <StyledAccordionSummary>
                <SectionLabel>Find Audiences</SectionLabel>
              </StyledAccordionSummary>
              <AreaSearch
                groupsById={groupsById}
                ActionTargetVariables={ActionTargetVariables}
                onActionTargetVariableChange={onActionTargetVariableChange}
                ActionTargetSubVariables={ActionTargetSubVariables}
                handleActionTargetSubVariableChange={handleActionTargetSubVariableChange}
                ActionMode={ActionMode}
                ActionTargetConcentration={ActionTargetConcentration}
                handleActionTargetConcentrationChange={handleActionTargetConcentrationChange}
                selectedDemographics={selectedDemographics}
                selectedFeatureData={selectedFeatureData}
              />
            </StyledAccordion>

            {/* Top ranked areas */}
            <StyledAccordion defaultExpanded>
              <StyledAccordionSummary>
                <SectionLabel>{compareWaves ? "Top Areas by Movement" : "Top Areas"}</SectionLabel>
              </StyledAccordionSummary>
              <TopAreasPanel
                items={rankedItems}
                onSelect={(area) => onAreaSelect?.(area)}
                initialCount={10}
                compareMode={compareWaves}
              />
            </StyledAccordion>

            {/* Target demographics */}
            <StyledAccordion defaultExpanded>
              <StyledAccordionSummary>
                <SectionLabel>Target Demographics</SectionLabel>
              </StyledAccordionSummary>
              <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0.5 }}>
                <Typography sx={{ fontSize: "12px", color: T.textFaint, mb: 1.75, fontFamily: "'DM Sans', sans-serif" }}>
                  Choose one or more groups to refine your audience.
                </Typography>
                <Stack spacing={1.75}>
                  <Stack direction="row" spacing={2}>
                    <Box sx={{ flex: 1 }}>
                      <DemographicGroup icon={PersonIcon} label="Gender" iconColor="#60a5fa">
                        {DEMO_FILTER_OPTIONS.gender.map(([val, lbl]) => (
                          <FilterChip key={val} label={lbl} active={isSel(genderFilter, val)}
                            onClick={() => onGenderChange?.(toggleVal(genderFilter, val))} />
                        ))}
                      </DemographicGroup>
                    </Box>
                    <Box sx={{ flex: 1.5 }}>
                      <DemographicGroup icon={CakeIcon} label="Age" iconColor="#a78bfa">
                        {DEMO_FILTER_OPTIONS.age.map(([val, lbl]) => (
                          <FilterChip key={val} label={lbl} active={isSel(ageFilter, val)}
                            onClick={() => onAgeChange?.(toggleVal(ageFilter, val))} />
                        ))}
                      </DemographicGroup>
                    </Box>
                  </Stack>

                  <DemographicGroup icon={SchoolIcon} label="Education" iconColor="#fbbf24">
                    {DEMO_FILTER_OPTIONS.education.map(([val, lbl]) => (
                      <FilterChip key={val} label={lbl} active={isSel(educationFilter, val)}
                        onClick={() => onEducationChange?.(toggleVal(educationFilter, val))} />
                    ))}
                  </DemographicGroup>

                  <DemographicGroup icon={GroupsIcon} label="Ethnicity" iconColor="#34d399">
                    {DEMO_FILTER_OPTIONS.ethnicity.map(([val, lbl]) => (
                      <FilterChip key={val} label={lbl} active={isSel(ethnicityFilter, val)}
                        onClick={() => onEthnicityChange?.(toggleVal(ethnicityFilter, val))} />
                    ))}
                  </DemographicGroup>

                  <DemographicGroup icon={HowToVoteIcon} label="Past Vote" iconColor="#f87171">
                    {DEMO_FILTER_OPTIONS.party.map(([val, lbl]) => (
                      <FilterChip key={val} label={lbl} active={isSel(partyFilter, val)}
                        onClick={() => onPartyChange?.(toggleVal(partyFilter, val))} />
                    ))}
                  </DemographicGroup>
                </Stack>

                {onClearAll && (
                  <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                    <ButtonBase
                      onClick={onClearAll}
                      sx={{
                        fontSize: "11px", color: T.textFaint,
                        fontFamily: "'DM Sans', sans-serif", fontWeight: 500,
                        letterSpacing: "0.03em", px: 1.25, py: 0.5,
                        borderRadius: "6px", border: `1px solid ${T.border}`,
                        transition: "all 0.15s",
                        "&:hover": { color: T.text, borderColor: T.borderStrong, bgcolor: T.surfaceHover },
                      }}
                    >
                      Clear all
                    </ButtonBase>
                  </Box>
                )}
              </AccordionDetails>
            </StyledAccordion>
          </Box>
        )}
      </Box>

      {/* ── Collapse toggle ───────────────────────────────────────────────── */}
      <Tooltip title={isPanelOpen ? "Collapse panel" : "Expand panel"} placement="right" arrow>
        <Box
          role="button"
          tabIndex={0}
          aria-label={isPanelOpen ? "Hide panel" : "Show panel"}
          onClick={togglePanel}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && togglePanel()}
          sx={{
            position: "relative", height: "100%", width: "28px",
            display: "flex", alignItems: "center", justifyContent: "center",
            bgcolor: T.surface, borderLeft: `1px solid ${T.border}`,
            boxShadow: "4px 0 12px rgba(0,0,0,0.06)",
            cursor: "pointer", zIndex: 2000,
            transition: "background 0.15s",
            "&:hover": { bgcolor: T.surfaceHover },
          }}
        >
          <Box sx={{
            width: 20, height: 20, borderRadius: "50%",
            display: "flex", alignItems: "center", justifyContent: "center",
            bgcolor: T.chip, border: `1px solid ${T.borderStrong}`,
          }}>
            {isPanelOpen
              ? <ChevronRightIcon sx={{ fontSize: 14, color: T.textFaint }} />
              : <ChevronLeftIcon sx={{ fontSize: 14, color: T.textFaint }} />
            }
          </Box>
        </Box>
      </Tooltip>
    </Box>
  );
};

export default Sidebar;
